import React, { useState } from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Text,
    Box,
    Icon,
    ModalCloseButton,
    SimpleGrid,
    VStack,
    useColorModeValue,
    Heading,
} from "@chakra-ui/react";
import { HiOutlineRefresh, HiOutlineHome, HiOutlineExclamationCircle } from "react-icons/hi";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

interface ChangeFlowModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (targetFlow: string) => void;
    documentId: string;
    loading?: boolean;
}

const ChangeFlowModal: React.FC<ChangeFlowModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    documentId,
    loading = false,
}) => {
    const [selectedFlow, setSelectedFlow] = useState("Guest House");

    const flowOptions = [
        { value: "Guest House", label: "Guest House", icon: HiOutlineHome },
        { value: "Payment Failed", label: "Payment Failed", icon: HiOutlineExclamationCircle },
    ];

    const bg = useColorModeValue("white", "gray.800");
    const borderColor = useColorModeValue("gray.200", "gray.700");
    const activeBorderColor = useColorModeValue("teal.500", "teal.300");
    const activeBg = useColorModeValue("teal.50", "rgba(49, 151, 149, 0.1)");

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
            <ModalOverlay backdropFilter="blur(4px)" />
            <ModalContent borderRadius="xl" overflow="hidden" boxShadow="2xl" bg={bg}>
                <ModalHeader
                    bg={useColorModeValue("gray.50", "gray.900")}
                    borderBottomWidth="1px"
                    borderColor={borderColor}
                    py={4}
                >
                    <VStack align="start" spacing={0}>
                        <Heading size="md" color="teal.500">Change Document Flow</Heading>
                        <Text fontSize="xs" color="gray.500" fontWeight="normal">
                            Document ID: {documentId}
                        </Text>
                    </VStack>
                </ModalHeader>
                <ModalCloseButton mt={2} />

                <ModalBody py={8}>
                    <Text mb={6} fontSize="sm" color="gray.600" textAlign="center">
                        Select the new target flow for this document. This will update its processing track.
                    </Text>

                    <SimpleGrid columns={2} spacing={4}>
                        {flowOptions.map((option) => {
                            const isSelected = selectedFlow === option.value;
                            return (
                                <MotionBox
                                    key={option.value}
                                    as="button"
                                    onClick={() => setSelectedFlow(option.value)}
                                    whileHover={{ y: -4, boxShadow: "lg" }}
                                    whileTap={{ scale: 0.98 }}
                                    p={6}
                                    borderRadius="lg"
                                    borderWidth="2px"
                                    borderColor={isSelected ? activeBorderColor : borderColor}
                                    bg={isSelected ? activeBg : "transparent"}
                                    transition="all 0.2s"
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    gap={3}
                                    position="relative"
                                >
                                    <Icon
                                        as={option.icon}
                                        boxSize={8}
                                        color={isSelected ? activeBorderColor : "gray.400"}
                                    />
                                    <Text
                                        fontWeight="bold"
                                        color={isSelected ? "teal.600" : "gray.600"}
                                        fontSize="md"
                                    >
                                        {option.label}
                                    </Text>
                                    {isSelected && (
                                        <Box
                                            position="absolute"
                                            top={2}
                                            right={2}
                                            w={3}
                                            h={3}
                                            borderRadius="full"
                                            bg="teal.500"
                                        />
                                    )}
                                </MotionBox>
                            );
                        })}
                    </SimpleGrid>
                </ModalBody>

                <ModalFooter bg={useColorModeValue("gray.50", "gray.900")} borderTopWidth="1px" borderColor={borderColor} gap={3}>
                    <Button
                        variant="ghost"
                        onClick={onClose}
                        fontWeight="medium"
                    >
                        Cancel
                    </Button>
                    <Button
                        colorScheme="teal"
                        px={8}
                        isLoading={loading}
                        onClick={() => onConfirm(selectedFlow)}
                        boxShadow="md"
                        _hover={{ boxShadow: "lg" }}
                    >
                        Update Flow
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ChangeFlowModal;

